import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ADGetTasks } from "../Service/AllTaskService";
import DropdownPortal from "../../../../DropDownPortal";
import ADInProgressTaskModal from "../Modal/ADInProgressTaskModal";
import ADHoldTaskModal from "../Modal/ADHoldTaskModal";
import ReassignTaskModal from "../../../../Task/Modal/ReassignTaskModal";
import ViewTaskModal from "../../../../Task/Modal/ViewTaskModal";
import TaskHistoryModal from "../../../../Task/Modal/TaskHistoryModal";
import FilterTableModal from "../../../../Modal/FilterTableModal";
import CreateTaskModal from "../../../../Task/Modal/CreateTaskModal";
import ADInvalidTaskModal from "../Modal/ADInvalidTaskModal";
import { useSearchParams, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import TableHeader from "../../../../Common/TableHeader";
import Pagination from "../../../../Common/Pagination";
import { globalSearch } from "../../../../Utils/globalSearch";

const getSeverityStyle = (severity) => {
  switch (severity) {
    case "High":
      return "bg-red-100 text-red-700";
    case "Moderate":
      return "bg-yellow-100 text-yellow-700";
    case "Low":
      return "bg-green-100 text-green-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const ADAssignedTask = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);

  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const [viewModal, setViewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [inProgressTaskData, setInProgressTaskData] = useState(null);

  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdTaskData, setHoldTaskData] = useState(null);

  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reAssignTaskData, setReAssignTaskData] = useState(null);

  const [showInvalidTaskModal, setShowInvalidTaskModal] = useState(false);
  const [invalidTaskData, setInvalidTaskData] = useState(null);

  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterField, setFilterField] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  //Open modal through URL
  const [searchParams] = useSearchParams();
  const openTaskNo = searchParams.get("open");

  // Re-assign Dropdown
  const [isPM, setIsPM] = useState(false);
  const [openCreateFromCopy, setOpenCreateFromCopy] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery, rowsPerPage]);

  //for Copy task
  useEffect(() => {
    const handler = () => setOpenCreateFromCopy(true);
    window.addEventListener("openCreateTaskWithCopy", handler);

    return () => window.removeEventListener("openCreateTaskWithCopy", handler);
  }, []);

  const loadTasks = async () => {
    try {
      const response = await ADGetTasks(1);
      const data = response.data;

      if (data.status === "Success") {
        const formatted = data.tasks.map((t, index) => ({
          id: index + 1,
          ...t,
        }));

        setTasks(formatted);

        const filtered = applyFiltersAndSearch(formatted, filters, searchQuery);

        setFilteredTasks(filtered);
        setCurrentPage(1);

        if (openTaskNo) {
          const match = formatted.find(
            (x) => String(x.taskUID) === String(openTaskNo),
          );
          if (match) {
            setSelectedTask(match);
            setViewModal(true);
          }
        }
      }
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      console.error("Error fetching tasks:", err);
    }
  };

  useEffect(() => {
    const pm = Cookies.get("Orbit_PMCount");
    if (pm && Number(pm) > 0) {
      setIsPM(true);
    } else {
      setIsPM(false);
    }
  }, []);

  const formatDateTime = (dateString) => {
    if (!dateString) return "--";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) return "--";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  const closeModal = () => {
    setViewModal(false);
    window.history.replaceState(null, "", "/Task/Assigned-Task");
  };

  // Filtering Functions
  const getUniqueValues = (key) => {
    const values = tasks.map((t) => t[key] ?? "--");
    return [...new Set(values)];
  };

  const openFilter = (field) => {
    setFilterOptions(getUniqueValues(field));
    setFilterField(field);
    setFilterModalOpen(true);
  };

  const applyColumnFilter = (field, selectedValues) => {
    const updatedFilters = { ...filters, [field]: selectedValues };
    setFilters(updatedFilters);

    let result = tasks;

    // Apply Filters
    Object.keys(updatedFilters).forEach((f) => {
      if (updatedFilters[f].length > 0) {
        result = result.filter((row) =>
          updatedFilters[f].includes(row[f] ?? "--"),
        );
      }
    });

    // Apply Search
    const query = searchQuery.toLowerCase();
    if (query !== "") {
      result = result.filter((task) =>
        JSON.stringify(task).toLowerCase().includes(query),
      );
    }

    setFilteredTasks(result);
  };

  //To Avoid filters remove after action
  const applyFiltersAndSearch = (sourceTasks, filters, searchQuery) => {
    let result = [...sourceTasks];

    // Apply Filters
    Object.keys(filters).forEach((f) => {
      if (filters[f]?.length > 0) {
        result = result.filter((row) => filters[f].includes(row[f] ?? "--"));
      }
    });
    // Apply Search
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      result = result.filter((task) =>
        JSON.stringify(task).toLowerCase().includes(q),
      );
    }

    return result;
  };

 const handleSearch = (query) => {
  setSearchQuery(query);
  setFilteredTasks(globalSearch(tasks, query));
};

  const rowsToShow =
    searchQuery !== "" || Object.values(filters).some((x) => x.length > 0)
      ? filteredTasks
      : tasks;

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const currentRows = rowsToShow.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(rowsToShow.length / rowsPerPage);

  const startIndex = rowsToShow.length === 0 ? 0 : indexOfFirstRow + 1;
  const endIndex = Math.min(indexOfLastRow, rowsToShow.length);

  useEffect(() => {
    const close = () => setOpenActionMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/*  Header Section */}
      <TableHeader
        searchQuery={searchQuery}
        onSearch={handleSearch}
        onCreate={() => setShowModal(true)}
        placeholder="Search for tasks"
        createLabel="+ Create Task"
        totalRecords={rowsToShow.length}
        startIndex={startIndex}
        endIndex={endIndex}
        rowsPerPage={rowsPerPage}
        onPageSizeChange={setRowsPerPage}
      />

      {/* Table */}
      <div className="overflow-x-auto overflow-visible bg-white rounded-lg shadow-sm border border-gray-200">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
            <tr>
              <th className="p-3">Task No.</th>
              <th className="p-3">
                <div className="flex gap-1 items-center">
                  Project Name
                  <Filter
                    size={14}
                    className="cursor-pointer"
                    onClick={() => openFilter("projectName")}
                  />
                  {filters["projectName"]?.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-2 rounded-full text-xs">
                      {filters["projectName"].length}
                    </span>
                  )}
                </div>
              </th>

              <th className="p-3">
                <div className="flex gap-1 items-center">
                  Assigned To
                  <Filter
                    size={14}
                    className="cursor-pointer"
                    onClick={() => openFilter("assignedTo")}
                  />
                  {filters["assignedTo"]?.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-2 rounded-full text-xs">
                      {filters["assignedTo"].length}
                    </span>
                  )}
                </div>
              </th>

              <th className="p-3">Updated At</th>

              <th className="p-3">
                <div className="flex gap-1 items-center">
                  Priority
                  <Filter
                    size={14}
                    className="cursor-pointer"
                    onClick={() => openFilter("severity")}
                  />
                  {filters["severity"]?.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-2 rounded-full text-xs">
                      {filters["severity"].length}
                    </span>
                  )}
                </div>
              </th>

              <th className="p-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {currentRows.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-6 text-center text-gray-500">
                  No assigned tasks found.
                </td>
              </tr>
            ) : (
              currentRows.map((task) => (
                <tr key={task.id} className="border-b hover:bg-gray-50">
                  <td
                    className="p-3 font-medium text-blue-600 cursor-pointer hover:underline"
                    onClick={() => {
                      window.history.replaceState(
                        null,
                        "",
                        `?open=${task.taskUID}`,
                      );
                      setSelectedTask(task);
                      setViewModal(true);
                    }}
                  >
                    {task.taskNo}
                  </td>

                  <td className="p-3">{task.projectName}</td>
                  <td className="p-3">{task.assignedTo || "--"}</td>
                  <td className="p-3">{formatDateTime(task.lastUpdatedAt)}</td>

                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityStyle(
                        task.severity,
                      )}`}
                    >
                      {task.severity}
                    </span>
                  </td>

                  <td className="p-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(
                          openActionMenu === task.id ? null : task.id,
                        );
                        setMenuPosition({ x: e.clientX, y: e.clientY });
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <MoreVertical size={18} />
                    </button>
                  </td>

                  {openActionMenu === task.id && (
                    <DropdownPortal>
                      <div
                        style={{
                          position: "fixed",
                          top: menuPosition.y + 10,
                          left: menuPosition.x - 160,
                          zIndex: 99999,
                        }}
                        className="w-44 bg-white border rounded-lg shadow-xl py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowInProgressModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Mark In-Progress
                        </button>

                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowHoldModal(true);
                            setOpenActionMenu(null);
                          }}
                        >
                          Hold Task
                        </button>
                        {isPM && (
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                            onClick={() => {
                              setSelectedTask(task);
                              setShowReassignModal(true);
                              setOpenActionMenu(null);
                            }}
                          >
                            Re-Assign Task
                          </button>
                        )}
                        {isPM && (
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                            onClick={() => {
                              setSelectedTask(task);
                              setShowInvalidTaskModal(true);
                              setOpenActionMenu(null);
                            }}
                          >
                            Invalid Task
                          </button>
                        )}
                      </div>
                    </DropdownPortal>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        show={showModal || openCreateFromCopy}
        onClose={() => {
          setShowModal(false);
          setOpenCreateFromCopy(false);
        }}
        onCreated={loadTasks}
      />

      {/* Filter Modal */}
      <FilterTableModal
        show={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        field={filterField}
        options={filterOptions}
        activeSelections={filters[filterField] || []}
        onApply={applyColumnFilter}
        onResetAll={() => {
          setFilters({});
          setFilteredTasks(tasks);
        }}
      />

      {/* Modals */}
      <ViewTaskModal
        show={viewModal}
        onClose={closeModal}
        onOpenHistory={(history) => {
          setViewModal(false);
          setHistoryData(history);
          setHistoryModal(true);
        }}
        task={selectedTask}
        onInProgress={(task) => {
          setInProgressTaskData(task);
          setShowInProgressModal(true);
        }}
        onHold={(task) => {
          setHoldTaskData(task);
          setShowHoldModal(true);
        }}
        onReassign={(task) => {
          setReAssignTaskData(task);
          setShowReassignModal(true);
        }}
        onInvalid={(task) => {
          setInvalidTaskData(task);
          setShowInvalidTaskModal(true);
        }}
      />

      <TaskHistoryModal
        show={historyModal}
        onClose={() => {
          setHistoryModal(false);
          setViewModal(true);
        }}
        history={historyData}
      />

      <ADInProgressTaskModal
        show={showInProgressModal}
        onClose={() => setShowInProgressModal(false)}
        taskData={selectedTask}
        onInProgress={loadTasks}
      />

      <ADHoldTaskModal
        show={showHoldModal}
        onClose={() => setShowHoldModal(false)}
        taskData={selectedTask}
        onHold={loadTasks}
      />

      <ReassignTaskModal
        show={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        taskData={selectedTask}
        onReassign={loadTasks}
      />
      <ADInvalidTaskModal
        show={showInvalidTaskModal}
        onClose={() => setShowInvalidTaskModal(false)}
        taskData={selectedTask}
        onInvalid={loadTasks}
      />
    </div>
  );
};

export default ADAssignedTask;
